import React from 'react';

import { IMatchRow } from '../components/molecules/match-row/match-row';
import t from '../labels/labelling-utils';

import {
  getMatchEndedLabel,
  getWinnerLoserTeams,
  isEnded,
  isFinal,
  isLive,
  isSecondLeg,
  MatchEndedLabel,
} from './match-status';

// HEADER HELPER returning JSX/HTML
export const getHeader = (match: IMatchRow, forceGoalsResult?: boolean): React.ReactNode | null => {
  const objHeader: IHeaderFooter = getHeaderData(match, forceGoalsResult);
  const { team, label, status, value } = objHeader;

  if (!(objHeader.label || objHeader.value)) {
    return null;
  }

  return (
    <React.Fragment>
      {objHeader.label && (
        <span data-text="label">
          {team && `${team} `}
          {label && `${label} `}
          {status}
          {value && ': '}
        </span>
      )}
      {value && <span data-text="value">{value}</span>}
    </React.Fragment>
  );
};

// FOOTER HELPER returning JSX/HTML
export const getFooter = (match: IMatchRow): React.ReactNode => {
  const objFooter: IFooter = getFooterData(match);
  // @ts-ignore
  const { DTR, DTS, DCR, TOT }: MatchEndedLabel = objFooter;

  return DTR || DTS || DCR || TOT ? (
    <React.Fragment>
      {DTR && (
        <span data-state="dtr">
          {DTR.label}
          {DTR.label && ': '}
          {DTR.value}
        </span>
      )}
      {DTS && (
        <span data-state="dts">
          {DTS.label}
          {DTS.label && ': '}
          {DTS.value}
        </span>
      )}
      {DCR && (
        <span data-state="dcr">
          {DCR.label}
          {DCR.label && ': '}
          {DCR.value}
        </span>
      )}
      {TOT && (
        <span data-state="tot">
          {TOT.label}
          {TOT.label && ': '}
          {TOT.value}
        </span>
      )}
    </React.Fragment>
  ) : (
    ''
  );
};

// TS HELPERS

// HEADER/FOOTER
export interface IHeaderFooter {
  label: string | null;
  value: string | null;
  team?: string | null;
  status?: string | null;
}

// HEADER
export const getHeaderData = (match: IMatchRow, forceGoalsResult: boolean = false): IHeaderFooter => {
  const { home, away, competitionPhase, competitionPhaseRound, competitionPhaseWinnerTeamId } = match;
  const matchEndedLabel = getMatchEndedLabel(match);

  // Each KEY returns a FUNCTION returning a IHeaderFooter object
  type HeaderData = {
    [key in MatchEndedLabel]: () => IHeaderFooter;
  };

  // Using an OBJECT LITERAL instead of a SWITCH statement
  const headerData: HeaderData = {
    DTR: () => {
      const { winner } = getWinnerLoserTeams(match);
      return winner && competitionPhaseWinnerTeamId
        ? {
            team: winner.name,
            label: isFinal(competitionPhase, competitionPhaseRound)
              ? t('match.status.winner.knockout')
              : t('match.status.winner.secondleg'),
            status: null,
            value: null,
          }
        : {
            // FALLBACK if TEAM is NULL/UNDEFINED or DIFFERENT from the one in HOME/AWAY
            label: null, // Don't write "DTR" in HEADER in this case
            value: null,
          };
    },
    DTS: () => {
      const { winner } = getWinnerLoserTeams(match);
      return winner && competitionPhaseWinnerTeamId && !forceGoalsResult
        ? {
            team: winner.name,
            label: isFinal(competitionPhase, competitionPhaseRound)
              ? t('match.status.winner.knockout')
              : t('match.status.winner.secondleg'),
            status: t('match.status.winner.after.extra.time'),
            value: null,
          }
        : {
            // FALLBACK if TEAM is NULL/UNDEFINED or DIFFERENT from the one in HOME/AWAY
            label: t('match.status.after.extra.time.shortname'),
            value: null,
          };
    },
    DCR: () => {
      const { winner } = getWinnerLoserTeams(match);
      return winner && competitionPhaseWinnerTeamId && !forceGoalsResult
        ? {
            team: winner.name,
            label: isFinal(competitionPhase, competitionPhaseRound)
              ? t('match.status.winner.knockout')
              : t('match.status.winner.secondleg'),
            status: t('match.status.winner.after.shootout.time'),
            value: `${home.shootoutGoal || 0}-${away.shootoutGoal || 0}`,
          }
        : {
            // FALLBACK if TEAM is NULL/UNDEFINED or DIFFERENT from the one in HOME/AWAY
            label: t('match.status.after.shootout.time.shortname'),
            value: `${home.shootoutGoal || 0}-${away.shootoutGoal || 0}`,
          };
    },
    TR: () => {
      return {
        label: null,
        value: null,
      };
    },
    TS: () => {
      return {
        label: null,
        value: null,
      };
    },
    CR: () => {
      return {
        label: null,
        value: null,
      };
    },
  };

  return headerData[matchEndedLabel]();
};

// FOOTER
export interface IFooter {
  DTR?: IHeaderFooter | null;
  DTS?: IHeaderFooter | null;
  TOT?: IHeaderFooter | null;
  DCR?: IHeaderFooter | null; // ISS-6698
}

export const getFooterData = (
  match: Pick<IMatchRow, 'home' | 'away' | 'status' | 'competitionPhase' | 'competitionPhaseRound'>,
  isMatchChange = false
): IFooter => {
  const { home, away, status, competitionPhase, competitionPhaseRound } = match;

  // Do NOT show FOOTER is is PREMATCH, ABANDONED etc.
  if (!isLive(status) && !isEnded(status)) {
    return {};
  }

  // All GOALS are OPTIONAL in IResult interface
  // I use DESTRUCTURING with both RENAMING and ASSIGNING DEFAULT VALUES
  const {
    /* istanbul ignore next: default value */ regularTimeGoal: homeRegularTimeGoal = 0,
    /* istanbul ignore next: default value */ totalGoalLegs: homeTotalGoalLegs = 0,
    /* istanbul ignore next: default value */ overtimeGoal: homeOverTimeGoal = 0,
    /* istanbul ignore next: default value */ shootoutGoal: homeShootOutGoal = 0,
  } = home;
  const {
    /* istanbul ignore next: default value */ regularTimeGoal: awayRegularTimeGoal = 0,
    /* istanbul ignore next: default value */ totalGoalLegs: awayTotalGoalLegs = 0,
    /* istanbul ignore next: default value */ overtimeGoal: awayOverTimeGoal = 0,
    /* istanbul ignore next: default value */ shootoutGoal: awayShootOutGoal = 0,
  } = away;
  const matchEndedLabel = getMatchEndedLabel(match);

  // Using an OBJECT LITERAL instead of a SWITCH statement
  type FooterData = {
    [key in MatchEndedLabel]: () => IFooter;
  };

  const footerData: FooterData = {
    DTR: () => {
      return {
        TOT: isSecondLeg(competitionPhase, competitionPhaseRound)
          ? {
              label: t('match.status.aggregate.shortname'),
              value: `${homeTotalGoalLegs}-${awayTotalGoalLegs}`,
            }
          : null,
      };
    },
    DTS: () => {
      const dts: IFooter = {
        DTR: {
          label: t('match.status.after.regular.time.shortname'),
          value: `${homeRegularTimeGoal}-${awayRegularTimeGoal}`,
        },
        TOT: isSecondLeg(competitionPhase, competitionPhaseRound)
          ? {
              label: t('match.status.aggregate.shortname'),
              value: `${homeTotalGoalLegs}-${awayTotalGoalLegs}`,
            }
          : null,
      };
      return isMatchChange
        ? {
            ...dts,
            // ISS-6698
            DTS: {
              label: t('match.status.after.extra.time.shortname'),
              value: `${homeRegularTimeGoal + homeOverTimeGoal}-${awayRegularTimeGoal + awayOverTimeGoal}`,
            },
          }
        : dts;
    },
    DCR: () => {
      const dcr: IFooter = {
        DTR: {
          label: t('match.status.after.regular.time.shortname'),
          value: `${homeRegularTimeGoal}-${awayRegularTimeGoal}`,
        },
        TOT: isSecondLeg(competitionPhase, competitionPhaseRound)
          ? {
              label: t('match.status.aggregate.shortname'),
              value: `${homeTotalGoalLegs}-${awayTotalGoalLegs}`,
            }
          : null,
      };
      return isMatchChange
        ? {
            ...dcr,
            // ISS-6698
            DCR: {
              label: t('match.status.after.shootout.time.shortname'),
              value: `${homeRegularTimeGoal + homeOverTimeGoal + homeShootOutGoal}-${
                awayRegularTimeGoal + awayOverTimeGoal + awayShootOutGoal
              }`,
            },
          }
        : dcr;
    },
    // DEFAULT CASE - TOT is ALWAYS visible if MATCH is both LIVE and a RETURN one
    TR: () => {
      return {
        TOT:
          isSecondLeg(competitionPhase, competitionPhaseRound) && (isLive(match.status) || isEnded(match.status))
            ? {
                label: t('match.status.aggregate.shortname'),
                value: `${homeTotalGoalLegs}-${awayTotalGoalLegs}`,
              }
            : null,
      };
    },
    // SHOW HEADER/FOOTER while MATCH is LIVE after FULLTIME
    TS: () => {
      return {
        DTR: {
          label: t('match.status.after.regular.time.shortname'),
          value: `${homeRegularTimeGoal}-${awayRegularTimeGoal}`,
        },
        TOT: isSecondLeg(competitionPhase, competitionPhaseRound)
          ? {
              label: t('match.status.aggregate.shortname'),
              value: `${homeTotalGoalLegs}-${awayTotalGoalLegs}`,
            }
          : null,
      };
    },
    CR: () => {
      return {
        DTR: {
          label: t('match.status.after.regular.time.shortname'),
          value: `${homeRegularTimeGoal}-${awayRegularTimeGoal}`,
        },
        DTS: {
          label: t('match.status.after.extra.time.shortname'),
          value: `${homeRegularTimeGoal + homeOverTimeGoal}-${awayRegularTimeGoal + awayOverTimeGoal}`,
        },
        TOT: isSecondLeg(competitionPhase, competitionPhaseRound)
          ? {
              label: t('match.status.aggregate.shortname'),
              value: `${homeTotalGoalLegs}-${awayTotalGoalLegs}`,
            }
          : null,
      };
    },
  };

  return footerData[matchEndedLabel]();
};
